/**
 * OEMConfig - Centralized OEM/Branding configuration
 * Makes it easy to rebrand the application
 * 
 * Now supports multi-tenant configurations loaded from /oem/{tenant}/oem.json
 */

import { Logger } from './Logger';
import { tenantService } from './TenantService';
import { fetchWithCacheBust } from '../utils/fetchUtils';

const logger = Logger.scope('OEMConfig');

interface OEMBranding {
  caption: string;
  fullName: string;
  website: string;
  appUrl: string;
  logo: {
    icon: string;
    splash: string;
    login?: string;
  };
  favicon: string;
  documentation: string;
  loginBackground?: string;
  loginTheme?: string;
  userProfile: {
    defaultAvatar: string;
  };
}

interface OEMCompany {
  name: string;
  logo?: string;
  copyright: string;
  website: string;
}

interface OEMTheme {
  /** Force a specific theme by name (e.g., 'dark', 'light', 'trisys'). Null = use user preference. */
  forcedMode: string | null;
  /** If false, hide the Themes section in sidebar entirely. */
  allowUserToggle: boolean;
  /** If false, Themes group in sidebar starts collapsed. Defaults to true. */
  themesExpanded?: boolean;
}

interface OEMFeatures {
  dashboardViews: string[];
  disabledForms: string[];
  settingsForm?: string;
  accountForm?: string;
}

/**
 * Session storage type for authentication tokens
 * - 'memory': Most secure - lost on page refresh (for banking apps)
 * - 'session': Survives refresh, lost when tab closes (default)
 * - 'local': Persists until explicitly cleared (for dev/convenience apps)
 */
export type SessionStorageType = 'memory' | 'session' | 'local';

/**
 * Backend/API configuration
 */
interface OEMBackend {
  /** Session storage type - 'memory' | 'session' | 'local'. Defaults to 'session'. */
  sessionStorage: SessionStorageType;
}

/**
 * App startup configuration for authentication requirements
 */
interface OEMApp {
  /** Form to display on app start. null = show app directly. "login" = show login form. */
  startScreen: string | null;
  /** If true, user must authenticate before seeing sidebar/app. */
  requireAuthentication: boolean;
  /** Which form handles login (default: "authentication"). */
  loginForm: string;
  /** Form to navigate to after successful login (default: "dashboard"). */
  postLoginForm: string;
}

/**
 * Auth provider configuration
 */
interface OEMAuth {
  /** Auth provider name (e.g., 'trisys', 'football-gateway') */
  provider: string;
  /** Provider-specific config */
  config?: {
    /** API SiteKey for the tenant */
    siteKey?: string;
    /** Other provider-specific settings */
    [key: string]: unknown;
  };
}

interface OEMConfiguration {
  tenant: string;
  auth?: OEMAuth;
  branding: OEMBranding;
  company: OEMCompany;
  theme?: OEMTheme;
  features?: OEMFeatures;
  backend?: OEMBackend;
  app?: OEMApp;
}

class OEMConfigService {
  private config: OEMConfiguration | null = null;

  /**
   * Load OEM configuration from tenant-specific JSON file
   */
  public async load(): Promise<void> {
    try {
      const configPath = tenantService.getFilePath('oem.json');
      logger.debug('Loading OEM config from:', configPath);
      
      const response = await fetchWithCacheBust(configPath);
      if (!response.ok) {
        throw new Error(`Failed to load OEM config: ${response.statusText}`);
      }
      this.config = await response.json();
      logger.debug('Configuration loaded for tenant:', this.config?.tenant);
    } catch (error) {
      logger.error('Error loading OEM configuration:', error);
      throw new Error(`OEM configuration is required. Please ensure ${tenantService.getFilePath('oem.json')} exists and is valid.`);
    }
  }

  /**
   * Get the full OEM configuration
   */
  public getConfig(): OEMConfiguration {
    if (!this.config) {
      throw new Error('OEM configuration not loaded. Call load() first.');
    }
    return this.config;
  }

  /**
   * Get the current tenant ID
   */
  public getTenantId(): string {
    return this.getConfig().tenant;
  }

  /**
   * Get branding information
   */
  public getBranding(): OEMBranding {
    return this.getConfig().branding;
  }

  /**
   * Get company information
   */
  public getCompany(): OEMCompany {
    return this.getConfig().company;
  }

  /**
   * Get theme configuration
   */
  public getTheme(): OEMTheme {
    return this.getConfig().theme || { forcedMode: null, allowUserToggle: true };
  }

  /**
   * Get features configuration
   */
  public getFeatures(): OEMFeatures {
    return this.getConfig().features || { dashboardViews: [], disabledForms: [], settingsForm: 'settings' };
  }

  /**
   * Get backend configuration
   */
  public getBackend(): OEMBackend {
    return this.getConfig().backend || { sessionStorage: 'session' };
  }

  /**
   * Get session storage type for authentication tokens
   * - 'memory': Most secure - lost on page refresh (for banking apps)
   * - 'session': Survives refresh, lost when tab closes (default)
   * - 'local': Persists until explicitly cleared (for dev/convenience apps)
   */
  public getSessionStorageType(): SessionStorageType {
    return this.getBackend().sessionStorage;
  }

  /**
   * Check if a form is disabled for the current tenant
   */
  public isFormDisabled(formName: string): boolean {
    return this.getFeatures().disabledForms.includes(formName);
  }

  /**
   * Check if a dashboard view is enabled for the current tenant
   */
  public isDashboardViewEnabled(viewName: string): boolean {
    const views = this.getFeatures().dashboardViews;
    // If no views specified, allow all
    if (views.length === 0) return true;
    return views.includes(viewName);
  }

  /**
   * Get enabled dashboard views
   */
  public getEnabledDashboardViews(): string[] {
    return this.getFeatures().dashboardViews;
  }

  /**
   * Get brand name
   */
  public getBrandName(): string {
    return this.getBranding().caption;
  }

  /**
   * Get full brand name
   */
  public getFullBrandName(): string {
    return this.getBranding().fullName;
  }

  /**
   * Get website URL
   */
  public getWebsite(): string {
    return this.getBranding().website;
  }

  /**
   * Get logo icon URL
   */
  public getLogoIcon(): string {
    return this.getBranding().logo.icon;
  }

  /**
   * Get splash image URL
   */
  public getSplashImage(): string {
    return this.getBranding().logo.splash;
  }

  /**
   * Get favicon URL
   */
  public getFavicon(): string {
    return this.getBranding().favicon;
  }

  /**
   * Get login background image URL
   */
  public getLoginBackground(): string | undefined {
    return this.getBranding().loginBackground;
  }

  /**
   * Get login logo URL
   */
  public getLoginLogo(): string | undefined {
    return this.getBranding().logo.login;
  }

  /**
   * Get login theme
   */
  public getLoginTheme(): string {
    return this.getBranding().loginTheme || 'dark';
  }

  /**
   * Get app startup configuration
   */
  public getApp(): OEMApp {
    return this.getConfig().app || {
      startScreen: null,
      requireAuthentication: false,
      loginForm: 'authentication',
      postLoginForm: 'dashboard'
    };
  }

  /**
   * Check if authentication is required before showing the app
   */
  public requiresAuthentication(): boolean {
    return this.getApp().requireAuthentication;
  }

  /**
   * Get the API SiteKey for this tenant
   * Falls back to default SiteKey if not configured
   */
  public getSiteKey(): string | null {
    return this.config?.auth?.config?.siteKey || null;
  }

  /**
   * Get the start screen setting
   */
  public getStartScreen(): string | null {
    return this.getApp().startScreen;
  }

  /**
   * Get the form to navigate to after successful login
   */
  public getPostLoginForm(): string {
    return this.getApp().postLoginForm;
  }

  /**
   * Get the login form name
   */
  public getLoginForm(): string {
    return this.getApp().loginForm;
  }
}

// Export singleton instance
export const oemConfig = new OEMConfigService();

